import {RouteLeafDraw} from "@/my-leaf/routes/route-leaf-draw";
import * as waypoints from "@/my-leaf/routes/components/route-waypoints";
import * as legs from "@/my-leaf/routes/components/route-legs"
import store from "@/store";
import L from "leaflet";
import {myAxios} from "@/services/myAxios";
import {eventBus} from "@/main";
import {prepareError} from "@/my-leaf/routes/components/route-errors";
import {useRoutesStore} from "@/stores/routes";
import {useVersionStore} from "@/stores/version";

import {refreshWaypointTooltips} from "@/my-leaf/routes/components/route-waypoints";
import {getDepthAsync} from "@/my-leaf/routes/components/route-utils"
import { isNumber, toNumber } from "lodash";
import { offsetGeneralRoute } from "@/utils/general-route"
import { GeoUtils } from "@/utils/geo-utils";
import { WaypointUtils } from "@/utils/waypoint-utils";

export class RouteLeafRouting extends RouteLeafDraw {

    bypassID1
    bypassID2

    async initbaserouting(el) {
        await super.initbasewp(el);
    }

    getAutoRoute(method) {
        let pts = []

        let bounds = L.latLngBounds(this.waypoints[0].getLatLng(), this.waypoints[1].getLatLng());
        this.map.fitBounds(bounds)

        this.waypoints.forEach(waypoint => {
            pts.push({
                lat: waypoint.getLatLng().lat,
                lon: waypoint.getLatLng().lng,
                radius: this.routesStore.getShipRadius,
                xteLeft: this.routesStore.getShipXtel,
                xteRight: this.routesStore.getShipXter
            })
        })
        this.startRouteCalculation(pts, [], true, false, false, 'none', method)
    }

    drawRouteSegment(points, color, weight = 3, opacity = 0.8, options) {
        const routeLines = WaypointUtils.drawSegments(points, () => ({
            color: color,
            weight: weight,
            opacity: opacity,
            ...(options || {})
        }));
        routeLines.forEach(l => l.addTo(this.previewLayerGroup));

        return routeLines;
    }

    drawPointMarker(point, radius = 5, color = '#3388ff', tooltip = '') {
        const marker = L.circleMarker([point.lat, point.lon], {
            radius: radius,
            fillColor: color,
            color: '#fff',
            weight: 1,
            opacity: 1,
            fillOpacity: 0.8
        });
        if (tooltip)
            marker.bindTooltip(tooltip, {permanent: true, direction: 'top', offset: L.point(0, -10)});
        this.previewLayerGroup.addLayer(marker);
        return marker;
    }

    generalRouteLineFromPoint(data, p) {
        const byDist = data.lines.map(line => {
            const dx = Math.abs(p.lon - line.points[0].lon);
            const dy = Math.abs(p.lat - line.points[0].lat);

            return {
                line,
                dist: dx*dx + dy*dy
            }
        }).sort((l,r) => l.dist - r.dist);
        return byDist[0]?.line;
    }

    drawGeneralRoute() {
        const routesStore = useRoutesStore();

        const settings = this.routesStore?.getShipSettings ?? {};
        const graphDiag = settings?.graphDiag;

        if (this.previewLayerGroup) {
            this.map.removeLayer(this.previewLayerGroup);
        }
        this.previewLayerGroup = L.layerGroup().addTo(this.map);

        const generalRoute = routesStore.generalRoute.data;
        if (!generalRoute) {
            this.waypoints.forEach((waypoint, i) => {
                let color = waypoints.getWaypointColor.call(this);
                const latLng = waypoint.getLatLng();
                const coordinates = L.latLng(latLng.lat, latLng.lng);
                let newMarker = new L.circleMarker(coordinates, {
                    bubblingMouseEvents: false,
                    id: this.waypoints.length,
                    autoPan: true,
                    radius: 4,
                    weight: 15,
                    opacity: 0,
                    fillOpacity: 0.8,
                    color: color,
                    isSelected: false,
                    wpStatus: 'none'
                }).addTo(this.previewLayerGroup);

                refreshWaypointTooltips(newMarker, i, this.waypoints.length);
            });
            return;
        } 

        this.previewStartPoint = generalRoute.startPoint;
        this.previewFinishPoint = generalRoute.finishPoint;
        
        offsetGeneralRoute(generalRoute).map((data, offsetIndex) => {
            const findLineFromPoint = (p) => this.generalRouteLineFromPoint(data, p);

            let mainRoutePolylines = [];
            data.lines.forEach(line => {
                const polylines = this.drawRouteSegment(line.points, '#000000');
                polylines.forEach(polyline => {
                    polyline.on('click', (e) => {
                        eventBus.$emit('general-route-segment-selected', line);
                        L.DomEvent.stop(e);
                    });
                });

                mainRoutePolylines.push(...polylines);

                line.points.forEach(point => {
                    this.drawPointMarker(point, 4);
                });
            });

            data.links.forEach(link => {
                const polylines = this.drawRouteSegment(link.points, '#0000ff', 3, 0.8, { dashArray: '5, 7' });
                polylines.forEach(polyline => {
                    polyline.on('click', (e) => {
                        const line = findLineFromPoint(link.points[link.points.length - 1]);
                        eventBus.$emit('general-route-segment-selected', line);
                        L.DomEvent.stop(e);
                    });
                })

                mainRoutePolylines.push(...polylines);

                link.points.forEach(point => {
                    this.drawPointMarker(point, 4);
                });
            });

            if (data.distance && mainRoutePolylines.length > 0) {
                const distanceKm = (data.distance / 1000).toFixed(1);
                mainRoutePolylines.forEach(polyline => {
                    polyline.bindTooltip(`Distance: ${distanceKm} km`, {
                        sticky: true,
                        direction: 'center'
                    });
                });
            }

            if (graphDiag) {
                if (data.entryPoints && data.entryPoints.length > 0) {
                    data.entryPoints.forEach((point, index) => {
                        const entryMarker = L.marker([point.lat, point.lon], {
                            icon: L.divIcon({
                                html: `<div style="color: green; font-size: 24px;">▲</div>`,
                                className: 'entry-point-marker',
                                iconSize: [20, 20],
                                iconAnchor: [10, 20]
                            })
                        });
                        this.previewLayerGroup.addLayer(entryMarker);


                        entryMarker.bindTooltip(`Entry ${index + 1}`, {
                            permanent: false,
                            direction: 'top',
                            offset: L.point(0, -15)
                        });
                    });
                }

                if (data.exitPoints && data.exitPoints.length > 0) {
                    data.exitPoints.forEach((point, index) => {
                        const exitMarker = L.marker([point.lat, point.lon], {
                            icon: L.divIcon({
                                html: `<div style="color: red; font-size: 24px;">▼</div>`,
                                className: 'exit-point-marker',
                                iconSize: [20, 20],
                                iconAnchor: [10, 0]
                            })
                        });
                        this.previewLayerGroup.addLayer(exitMarker);

                        exitMarker.bindTooltip(`Exit ${index + 1}`, {
                            permanent: false,
                            direction: 'bottom',
                            offset: L.point(0, 15)
                        });
                    });
                }

                if (data.alternativeRoutes && data.alternativeRoutes.length > 0) {

                    const altColors = ['#ff7f00', '#984ea3', '#4daf4a', '#e41a1c', '#377eb8'];

                    data.alternativeRoutes.forEach((route, index) => {
                        if (index === 0 && route.distance === data.distance) {
                            return;
                        }

                        const routeColor = altColors[index % altColors.length];
                        const altRoutePolylines = [];

                        route.lines.forEach(line => {
                            const polylines = this.drawRouteSegment(line.points, routeColor, 2, 0.5, { dashArray: '5, 7' });
                            altRoutePolylines.push(...polylines);
                        });

                        route.links.forEach(link => {
                            const polylines = this.drawRouteSegment(link.points, routeColor, 2, 0.5, { dashArray: '5, 7' });
                            altRoutePolylines.push(...polylines);
                        });

                        const distanceKm = (route.distance / 1000).toFixed(1);
                        altRoutePolylines.forEach(polyline => {
                            polyline.bindTooltip(`Alt ${index + 1}: ${distanceKm} km`, {
                                sticky: true,
                                direction: 'center'
                            });
                        });
                    });
                }

                this.drawPointMarker(data.startPoint, 6, '#00a651', "Start: " + (data.startNode?.nodeId || '') + "/" + (data.startNode?.pointIndex || ''));
                this.drawPointMarker(data.finishPoint, 6, '#ff0000', "Finish: " + (data.finishNode?.nodeId || '') + "/" + (data.finishNode?.pointIndex || ''));
            }
            else {
                this.drawPointMarker(data.startPoint, 6, '#00a651', "Start");
                this.drawPointMarker(data.finishPoint, 6, '#ff0000', "Finish");
            }

        });
    }

    debugPreviewData(label) {
        const routesStore = useRoutesStore();
        const previewData = routesStore.generalRoute?.data;
    }

    updateRoute() {
        const routesStore = useRoutesStore();
        const previewData = routesStore.generalRoute?.data;
        if (previewData) {
            this.waypoints = [];
            waypoints.addWP.call(this, L.latLng(previewData.startPoint.lat, previewData.startPoint.lon));
            waypoints.addWP.call(this, L.latLng(previewData.finishPoint.lat, previewData.finishPoint.lon));
            this.calculateGraphRoute('preview');
        }
    }

    clearGeneralRoute() {
        if (this.previewLayerGroup) {
            this.previewLayerGroup.clearLayers();
        }

        this.previewStartPoint = null;
        this.previewFinishPoint = null;


        const routesStore = useRoutesStore();
        if (routesStore) {
            routesStore.clearGeneralRoute();
            routesStore.clearBigDataRoute();
        }

        this.clearBigDataRouteLayers();
        if (this.wpGeneralRouteGroup) {
            this.wpGeneralRouteGroup.clearLayers();
        }

        eventBus.$emit('route-preview-cleared');
    }

    removeRouteWithDebug() {
        this.removeRoute();

        if (this.debugLayerGroup) {
            this.debugLayerGroup.clearLayers();
        }
    }

    copyToClipboard(payload) {
        try {
            navigator
                .clipboard
                .writeText(JSON.stringify(payload))
                .then(() => {})
                .catch(() => {});
        }
        catch {
            //
        }
    }

    makeRouteFromPreview() {
        if (this.previewStartPoint && this.previewFinishPoint) {
            this.startRouteCalculation(
                [
                    this.previewStartPoint,
                    this.previewFinishPoint
                ], 
                [], true, false, false, 'none', "graph");

            eventBus.$emit('wp-control-toggle-check-safety', false);               
        }
    }

    graphRouteURLAndPayload(config) {
        const { 
            type
        } = config;

        const startPoint = GeoUtils.normalizedPoint(config.startPoint ?? this.waypoints[0].getLatLng());
        const endPoint = GeoUtils.normalizedPoint(config.endPoint ?? this.waypoints[1].getLatLng())

        let graphParams = this.getGraphParams();
        if (!graphParams) {
            return null;
        } 

        const nogo = this.routesStore.getShipNoGo;
        const draught = this.routesStore.getShipDraught;
        const height = this.routesStore.getShipHeight;
        const width = this.routesStore.getShipWidth;
        const length = this.routesStore.getShipLength;
        const radius = this.routesStore.getShipRadius
        const xte_l = this.routesStore.getShipXtel
        const xte_r = this.routesStore.getShipXter
        const speed = this.routesStore.getShipSpeed
        const recommended = this.routesStore.getShipUserecommendedtracks
        const ratio = this.routesStore.getShipTrackratio
        const shallowratio = this.routesStore.getShipShallowratio
        const shallowstrength = this.routesStore.getShipAvoidshallowstrength
        const avoiddistance = this.routesStore.getShipAvoiddistance
        
        let nogoZones = []
        this.routesStore.nogoZones.forEach(item => {
            nogoZones.push(item)
        })

        const vesselParams = {
            length: length,
            breadth: width,
            height: height,
            draught: draught
        };

        let post_text = {
            "startPoint": {
                "lat": startPoint.lat,
                "lon": startPoint.lon
            },
            "endPoint": {
                "lat": endPoint.lat,
                "lon": endPoint.lon
            },
            "generalRouteOnly": true,
            "waitSeconds": 5,
            "params": {
                "depth": nogo,
                "height": height,
                "width": width,
                "length": length,
                "defaultXteLeft": xte_l,
                "defaultXteRight": xte_r,
                "defaultRadius": radius,
                "speedKnots": speed,
                "userecommendedTracks": recommended,
                "recommendedTrackCost": ratio,
                "shallowWaterCoeff": shallowratio,
                "shallowWaterCost": shallowstrength,
                "avoidDistance": avoiddistance,
                "restrictedZones": nogoZones
            },
            "vesselParams": vesselParams,
            "graphParams": graphParams
        }

        let URL = ''
        if (type === 'preview')
            URL = 'api/v1/route/multi_leg_request_preview'
        else if (type === 'full')
            URL = 'api/v1/route/multi_leg_request'
        else
            return null;

        return { URL, post_text }
    }
    
    async routeDepthChecks(config) {
        const { minDepth, startPoint, finishPoint, restrictedZones = [] } = config;
        if (!minDepth) return null;
        if (!startPoint) return "Start point was not provided";
        if (!finishPoint) return "Finish point was not provided";
        
        if (GeoUtils.isPointInsideZone(startPoint, restrictedZones)) return "Start point is inside restricted zone";
        if (GeoUtils.isPointInsideZone(finishPoint, restrictedZones)) return "Finish point is inside restricted zone";
        
        console.log("Min depth", minDepth);

        const startDepthString = await getDepthAsync({ lat: startPoint.lat, lng: GeoUtils.normalizedLon(startPoint.lon) });
        console.log("Start depth", startDepthString);
        const startDepth = toNumber(startDepthString);

        if (!isNumber(startDepth)) return "Cannot get depth for start point";
        if (startDepth === -1) return "Start point is on land";
        if (startDepth < minDepth) return `Start point depth (${startDepth} m) is less then required (${minDepth} m)`;
        
        const finishDepthString = await getDepthAsync({ lat: finishPoint.lat, lng: GeoUtils.normalizedLon(finishPoint.lon) });
        console.log("Finish depth", finishDepthString);
        const finishDepth = toNumber(finishDepthString);

        if (!isNumber(finishDepth)) return "Cannot get depth for finish point";
        if (finishDepth === -1) return "Finish point is on land"
        if (finishDepth < minDepth) return `Finish point depth (${finishDepth} m) is less then required (${minDepth} m)`;

        return null;
    }

    async calculateGraphRoute(type) {
        const data = this.graphRouteURLAndPayload({ type });
        if (!data) {
            this.clearRoute(false, true);  
            alert('ERROR');
            return;
        }

        eventBus.$emit('graph-route-calculation-begin', type)

        const { URL, post_text } = data;

        store.commit("routes/setCalculatingStatus", { status: true, title: 'route.calculation' });

        const routeDepthChecksResult = await this.routeDepthChecks({
            minDepth: post_text?.params?.depth,
            startPoint: post_text.startPoint,
            finishPoint: post_text.endPoint,
            restrictedZones: post_text.restrictedZones
        });

        if (routeDepthChecksResult) {
            this.clearRoute(true, true);  
            store.commit("routes/setRouteValidationMessage", routeDepthChecksResult);
            store.commit("routes/setCalculatingStatus", false)
            return false;
        }

        let progress = {
            progress: 0,
            progressMessage: ""
        }
        this.syntheticProgressUpdate(progress);

        let startTime = Date.now()

        this.copyToClipboard(post_text);
        
        const versionStore = useVersionStore();
        versionStore.checkAppVersion()
            .then(() => {
                myAxios.post(URL, post_text)
                    .then(resp => {
                        if ((resp.data.errorCode === 0 && resp.data.isSuccess && type === 'full') || (type === 'preview')) {

                            let time = Date.now() - startTime

                            eventBus.$emit('show-calculate-time', time / 1000)

                            this.clearRoute(false, true)

                            if (type === 'full')
                                this.routesStore.addShipRouteResponse(resp.data, false, false)

                            if (type === 'preview') {
                                if (!resp.data.isSuccess) {
                                    this.showError(resp.data)
                                }
                                else {
                                    this.routesStore.setGeneralRoute(resp.data);
                                    // copy general route route point to waypoints
                                    this.makeWaypointsFromGeneralRoute();
                                    this.drawMapLayers();
                                    this.fitBounds();
                                }
                            } else {
                                resp.data.calculatedRouteLegs.forEach(waypoint => {
                                    waypoints.addWP.call(this, L.latLng(waypoint.lat, waypoint.lon), -2)
                                })
                                legs.drawLegs.call(this, {
                                    updateRouteTable: true, 
                                    makeRouteTable: false
                                })

                                eventBus.$emit('is-need-check-safety', false)
                                eventBus.$emit('calculate-ukc')
                                eventBus.$emit('show-graphs')
                            }

                            store.commit("routes/setCalculatingStatus", false)
                        } else {
                            store.commit("routes/setCalculatingStatus", false)
                            this.showError(resp.data)
                        }
                    })
                    .catch((error) => {
                        console.log("error", error)
                        store.commit("routes/setCalculatingStatus", false)

                        this.showError(error)

                        store.commit("routes/setCalculatingStatus", false)
                        this.removeRouteWithDebug();
                        this.restoreContextAfterAuto();
                    });
            })
            .catch((error) => {
                console.log("error", error)
                store.commit("routes/setCalculatingStatus", false)

                this.showError(error)

                store.commit("routes/setCalculatingStatus", false)
                this.removeRouteWithDebug();
                this.restoreContextAfterAuto();
            });
    }

    calcucaltePathRoute(data) {
        data.startPoint.radius = 0
        data.startPoint.xteLeft = 0
        data.startPoint.xteRight = 0

        data.endPoint.radius = 0
        data.endPoint.xteLeft = 0
        data.endPoint.xteRight = 0

        const draught = this.routesStore.getShipNoGo;
        const height = this.routesStore.getShipHeight
        const radius = this.routesStore.getShipRadius
        const xte_l = this.routesStore.getShipXtel
        const xte_r = this.routesStore.getShipXter
        const speed = this.routesStore.getShipSpeed
        const recommended = this.routesStore.getShipUserecommendedtracks
        const ratio = this.routesStore.getShipTrackratio
        const shallowratio = this.routesStore.getShipShallowratio
        const shallowstrength = this.routesStore.getShipAvoidshallowstrength
        const avoiddistance = this.routesStore.getShipAvoiddistance

        data.params = {}
        data.params.depth = draught
        data.params.height = height
        data.params.defaultXteLeft = xte_l
        data.params.defaultXteRight = xte_r
        data.params.defaultRadius = radius
        data.params.speedKnots = speed
        data.params.userecommendedTracks = recommended
        data.params.recommendedTrackCost = ratio
        data.params.shallowWaterCoeff = shallowratio
        data.params.shallowWaterCost = shallowstrength
        data.params.avoidDistance = avoiddistance

        data.lines.forEach((line) => {
            line.points.forEach(point => {
                point.radius = 0
                point.xteLeft = 0
                point.xteRight = 0
            })
        })

        this.momentaryStore.setBusy1(true)

        let startTime = Date.now()
        let URL = 'api/v1/route/multi_leg_file'
        myAxios.post(URL, data)
            .then(resp => {
                if (resp.data.errorCode === 0 && resp.data.isSuccess) {

                    let time = Date.now() - startTime
                    eventBus.$emit('show-calculate-time', time / 1000)

                    this.clearRoute(false, true)

                    this.routesStore.addShipRouteResponse(resp.data, false, false)

                    resp.data.calculatedRouteLegs.forEach(waypoint => {

                        waypoints.addWP.call(this, L.latLng(waypoint.lat, waypoint.lon), -2)
                    })
                    legs.drawLegs.call(this, {
                        updateRouteTable: true, 
                        makeRouteTable: false
                    })

                    eventBus.$emit('is-need-check-safety', false)
                    eventBus.$emit('calculate-ukc')
                    eventBus.$emit('show-graphs')

                    this.momentaryStore.setBusy1(false)
                } else {
                    this.momentaryStore.setBusy1(false)
                    alert('Error: (' + resp.data.errorCode + ') Can\'t compute routes at the moment');
                }
            }).catch((error) => {
            this.momentaryStore.setBusy1(false)
            alert('Error: (' + error.status + ') \nCan\'t compute routes at the moment');

            this.momentaryStore.setBusy1(false)
            this.removeRouteWithDebug()
            this.restoreContextAfterAuto()
        })

    }

    async checkSafety() {
        let pointsSet = [[]];
        const maxDistance = 3000000; // 3K kms

        const pts = this.waypoints.map(waypoint => ({
            lat: waypoint.getLatLng().lat,
            lon: waypoint.getLatLng().lng,
            radius: this.routesStore.getShipRadius,
            xteLeft: this.routesStore.getShipXtel,
            xteRight: this.routesStore.getShipXter
        }));

        pts.forEach(pt => {
            if (GeoUtils.isCrossingDateLine(pointsSet.at(-1).at(-1), pt)) {
                pointsSet.push([]);
            }
            else {
                const nextDistance = GeoUtils.calculateDistanceByPoints([...pointsSet.at(-1), pt]);
                if (nextDistance >= maxDistance && pointsSet.at(-1).length > 2) 
                    pointsSet.push([pointsSet.at(-1).at(-1)]);
            }

            pointsSet.at(-1).push(pt);
        })

        pointsSet = pointsSet.filter(s => s.length);
        if (!pointsSet.length) return;

        //this.startRouteCalculation(pts, [], false);
        
        const startTime = Date.now();
        store.commit("routes/setCalculatingStatus", { status: true, title: 'route.safetyCalculation' });

        try {
            const routes = [];
            
            for (let pts of pointsSet) {
                const route = await this.startRouteCalculationAsync({
                    pts,
                    isAutoRoute: false,
                    progressType: "Safety check",
                    updateStore: false,
                    progressOffset: 100 * routes.length / pointsSet.length,
                    progressFactor: 1 / pointsSet.length,
                    waitForCompletion: false
                });

                routes.push(route);
            }

            // periodically check all routes

            const reportProgress = () => {
                let combinedProgress = 0;

                for (let i = 0; i < routes.length; i++) {
                    combinedProgress += (Math.max(0, routes[i].progress) ?? 0);
                }

                store.commit("routes/setProgress", {
                    progress: Math.floor(combinedProgress / routes.length),
                    text: `Safety check, please wait ... ${new Date().toLocaleTimeString()}`
                })
            }

            while (routes.some(r => r.status === "calculating")) {
                if (this.isAutoroutingCancel) {
                    this.isAutoroutingCancel = false;

                    for (let route of routes) {
                        await myAxios.delete("api/v1/route/" + route.id);
                    }

                    eventBus.$emit('wp-control-toggle-check-safety', true);
                    store.commit("routes/setCalculatingStatus", false);
                    return;
                }


                for (let i = 0; i < routes.length; i++) {
                    const route = routes[i];

                    if (route.status === "calculating") {
                        routes[i] = await this.checkRouteAsync({ 
                            routeId: route.id,
                            updateProgress: false,
                            updateStore: false
                        });
                    }

                    reportProgress();
                }

                await this.sleep(100);
            } 

            let result = routes[0];
            if (routes.length > 1) {
                // combine together separate responses
                // this code is a copy from server for similar functionality

                let allCalculatedLegs = routes.flatMap(r => r.calculatedRouteLegs);

                // shift all times and distances
                let currentMeters = 0;
                let currentSeconds = 0;

                for (let i = 1; i < allCalculatedLegs.length; i++)
                {
                    let prevLeg = allCalculatedLegs[i - 1];
                    let leg = allCalculatedLegs[i];
                    if (leg.info.fromStartSeconds == 0)
                    {
                        // add last point from previous segment to accumulator
                        currentMeters = prevLeg.info.fromStartMeters;
                        currentSeconds = prevLeg.info.fromStartSeconds;
                    }

                    leg.info.fromStartMeters += currentMeters;
                    leg.info.fromStartSeconds += currentSeconds;
                }

                let reverseLegs = [...allCalculatedLegs].reverse();
                currentMeters = 0;
                currentSeconds = 0;

                for (let i = 1; i < reverseLegs.length; i++)
                {
                    let prevLeg = reverseLegs[i - 1];
                    let leg = reverseLegs[i];
                    if (leg.info.toFinishSeconds == 0)
                    {
                        // add last point from previous segment to accumulator
                        currentMeters = prevLeg.info.toFinishMeters;
                        currentSeconds = prevLeg.info.toFinishSeconds;
                    }

                    leg.info.toFinishMeters += currentMeters;
                    leg.info.toFinishSeconds += currentSeconds;
                }            

                result = {
                    isSuccess: !!routes.length,
                    status: "done",
                    progress: 100,
                    debugInfo: [],
                    routeLength: routes.reduce((sum, item) => sum + item.routeLength, 0),
                    routePoints: [],
                    calculatedRouteLegs: []
                }

                // merge routes points and legs into single result
                result.routePoints.push(...routes[0].routePoints);
                result.calculatedRouteLegs.push(...routes[0].calculatedRouteLegs);

                for (let resp of routes.slice(1))
                {
                    let lastPoint = result.routePoints.at(-1);
                    let lastCalcPoint = result.calculatedRouteLegs.at(-1);

                    let firstSegmentPoint = resp.routePoints[0];

                    if (lastPoint != null && lastCalcPoint != null && lastPoint.isActual && !firstSegmentPoint.isActual)
                    {
                        // leg was not calculated, failed to calculate, timed out etc.
                        // set IsActual flag to false to indicate on the web
                        lastPoint.isActual = false;
                        lastCalcPoint.isActual = false;
                    }

                    if (GeoUtils.isCrossingDateLine(lastPoint, firstSegmentPoint)) {
                        result.routePoints.push(...resp.routePoints);
                        result.calculatedRouteLegs.push(...resp.calculatedRouteLegs);
                    }
                    else if (resp.calculatedRouteLegs[0].overallSafety === "unsafe" && lastCalcPoint.overallSafety !== "unsafe") {
                        console.log("remove previous safe point and replace with unsafe");
                        // remove previous safe point and replace with unsafe
                        result.routePoints.pop();
                        result.calculatedRouteLegs.pop();

                        result.routePoints.push(...resp.routePoints);
                        result.calculatedRouteLegs.push(...resp.calculatedRouteLegs);
                    }
                    else {
                        result.routePoints.push(...resp.routePoints.slice(1));
                        result.calculatedRouteLegs.push(...resp.calculatedRouteLegs.slice(1));
                    }
                }

                // correct leg indexes
                result.calculatedRouteLegs.forEach((l, lidx) => {
                    l.dangerObjects.forEach((d, didx) => {
                        d.legIndex = lidx;
                    })
                })
            }

            this.clearRoute(false, true)

            this.routesStore.addShipRouteResponse(result, false, false)

            result.calculatedRouteLegs.forEach(waypoint => {
                waypoints.addWP.call(this, L.latLng(waypoint.lat, waypoint.lon), -2)
            })
            legs.drawLegs.call(this, {
                updateRouteTable: true, 
                makeRouteTable: false
            })

            eventBus.$emit('is-need-check-safety', false)
            eventBus.$emit('calculate-ukc')
            eventBus.$emit('show-graphs')
            eventBus.$emit('switch-to-wp')

            this.drawMapLayers();

            eventBus.$emit('wp-control-toggle-check-safety', false);
        }
        catch (error) {
            this.showError(error);
        }

        store.commit("routes/setCalculatingStatus", false)

        let time = Date.now() - startTime;
        eventBus.$emit('show-calculate-time', time / 1000, 'route.safetyTime');
    }
    
    async sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
    
    async getRoute(routeId, method = 'standard') {
        return new Promise(resolve => {
            let URL = method === 'standard' ? '/api/v1/route/' : '/api/v1/graphRoute/'
            myAxios.get(URL + routeId)
                .then(resp => {
                    let progressMessage = resp.data.progressMessage || '';
                    const debugMarkers = ["Waiting for route", "Forward wave"];
                    if (debugMarkers.some(m => progressMessage.startsWith(m))) {
                        const routesStore = useRoutesStore();
                        const graphDiag = routesStore?.getShipSettings?.graphDiag;

                        if (!graphDiag) {
                            // this is diagnostic message, replace it with time counter
                            progressMessage = `Calculating route, please wait ... ${new Date().toLocaleTimeString()}`;
                        }
                    }
                    
                    const trimMarkers = ["Remaining routes"];
                    trimMarkers.forEach(marker => {
                        const position = progressMessage.indexOf(marker);
                        if (position >= 0) {
                            progressMessage = progressMessage.substr(0, position);
                        }
                    });

                    store.commit("routes/setProgress", {progress: resp.data.progress, text: progressMessage})

                    if (resp.data.status === 'calculating' && this.isAutoroutingCancel) {
                        store.commit("routes/setCalculatingStatus", false)

                        this.restoreContextAfterAuto()
                        this.isAutoroutingCancel = false
                        myAxios.delete("api/v1/graphRoute/" + routeId).then(resp => {
                        })
                        resolve(resp.data)
                    }

                    if (resp.data.status === 'calculating' && resp.data.restoreRoutePoints.length > 0) {
                        this.routesStore.addBigDataRoute(resp.data.restoreRoutePoints)
                        this.drawMapLayers();
                        this.fitBounds();
                    }

                    if (resp.data.status === 'calculating' && resp.data.routeLegs.length > 0) {
                        this.routesStore.addRouteLegs(resp.data.routeLegs)
                    }

                    if (resp.data.status === 'done') {
                        store.commit("routes/setCalculatingStatus", false)

                        let time = resp.data.duration

                        if (time) {
                            eventBus.$emit('show-calculate-time', time / 1000)
                        }

                        if (resp.data.isSuccess) {
                            if (this.routesStore.getShipResponseBackup.isBypass) {
                                let id1 = this.routesStore.getShipResponseBackup.startIndex
                                let id2 = this.routesStore.getShipResponseBackup.finishIndex
                                let prevResp = {...this.routesStore.getShipResponseBackup}
                                prevResp['routePoints'].splice(id1, id2 - id1, ...resp.data['routePoints'])

                                let idCount = 0;
                                prevResp['routePoints'].forEach(item => {
                                    item.id = idCount++
                                })
                                idCount = 0;
                                prevResp['calculatedRouteLegs'].forEach(item => {
                                    item.id = idCount++
                                })
                                resp.data = prevResp
                            }

                            this.clearRoute(false, true)

                            this.routesStore.addShipRouteResponse(resp.data, false, false)

                            resp.data.calculatedRouteLegs.forEach(waypoint => {

                                waypoints.addWP.call(this, L.latLng(waypoint.lat, waypoint.lon), -2)
                            })
                            legs.drawLegs.call(this, {
                                updateRouteTable: true, 
                                makeRouteTable: false
                            })

                            eventBus.$emit('is-need-check-safety', false)
                            eventBus.$emit('calculate-ukc')
                            eventBus.$emit('show-graphs')
                            eventBus.$emit('switch-to-wp')

                            this.drawMapLayers();
                        } else
                            this.showError(resp.data)

                        resolve(resp.data)
                    }

                    if (resp.data.status === 'calculating' && !this.isAutoroutingCancel) {
                        setTimeout(() => {
                            resolve(this.getRoute(routeId, method))
                        }, 1000);
                    }
                })
        })
    }

    showError(resp) {
        console.error(resp);
        let points = []
        prepareError(resp.errorCode, points, resp.exception)
    }

    getDefaultGraphParams() {

        const routesStore = useRoutesStore();
        const excludedZones = routesStore?.generalRoute?.excludedZones || [];
        const excludedIds = excludedZones.filter(z => (z.group || '') !== '').map(z => z.group);
        const bypassIds = excludedZones.filter(z => !z.group).map(z => z.id);

        let graphParams = {
            graphRouteScoreFactor: this.routesStore.getShipGraphRouteScoreFactor || 0.8,
            landAroundScoreFactor: this.routesStore.getShipLandAroundScoreFactor || 1.7,
            waterScoreFactor: this.routesStore.getShipWaterScoreFactor || 1,
            previewLandContour: this.routesStore.getShipPreviewLandContour !== undefined ? this.routesStore.getShipPreviewLandContour : false,
            nearestPointsNumber: this.routesStore.getShipNearestPointsNumber || 20,
            excludeZoneIds: excludedIds,
            bypassZoneIds: bypassIds,
            linkHeuristicFactor: this.routesStore.getShipLinkHeuristicFactor || 1
        };

        return graphParams;
    }

    getGraphParams() {
        // settings are now in panel, no need to ask operator
        let graphParams = this.getDefaultGraphParams();
        return graphParams;
    }

    routeCalculationURLAndPayload(config) {
        const {
            pts,
            restoreRoutePoints = [],
            isAutoRoute = true, 
            isGlobalRoute = false, 
            skipSafetyChecks = false,
            type = 'none', 
            method = 'standard'
        } = config;

        const nogo = this.routesStore.getShipNoGo;
        const draught = this.routesStore.getShipDraught;
        const height = this.routesStore.getShipHeight;
        const width = this.routesStore.getShipWidth;
        const length = this.routesStore.getShipLength;
        const radius = this.routesStore.getShipRadius
        const xte_l = this.routesStore.getShipXtel
        const xte_r = this.routesStore.getShipXter
        const speed = this.routesStore.getShipSpeed
        const recommended = this.routesStore.getShipUserecommendedtracks
        
        const vesselParams = {
            length: length,
            breadth: width,
            height: height,
            draught: draught
        };

        const routesStore = useRoutesStore();
        const generalRoute = routesStore.generalRoute.data;
        const generalRouteModified = routesStore.generalRoute.isModified;

        let generalroutesonly = this.routesStore.getShipUsegeneralroutes
        if (type === 'general') {
            generalroutesonly = true
        } else if (type === 'detailed')
            generalroutesonly = false

        let graphParams = this.getDefaultGraphParams();

        if (method === 'graph') {
            generalroutesonly = true

            graphParams = this.getGraphParams();
            if (!graphParams) {
                return null;
            }
        }

        const ratio = this.routesStore.getShipTrackratio
        const shallowratio = this.routesStore.getShipShallowratio
        const shallowstrength = this.routesStore.getShipAvoidshallowstrength
        const avoiddistance = this.routesStore.getShipAvoiddistance
        const usesuez = this.routesStore.getShipUsesuez
        const usepanama = this.routesStore.getShipUsepanama
        const usebering = this.routesStore.getShipUsebering
        const userivers = this.routesStore.getShipUserivers
        const refinecoeff = this.routesStore.getShipRefineCoeffecient

        let gRoute = []

        let post_text = {}

        let URL = method === 'standard' ? '/api/v1/route' : '/api/v1/graphRoute'

        let nogoZones = []
        this.routesStore.nogoZones.forEach(item => {
            nogoZones.push(item)
        })

        if (isGlobalRoute) {
            URL = '/api/v1/mss_route'
            let portStart = store.state.fickle.routes.globalRoute.ports[0]
            let portFinish = store.state.fickle.routes.globalRoute.ports[1]

            let portStartCoordinates = store.state.fickle.routes.globalRoute.portsCoordinates[0]
            let portFinishCoordinates = store.state.fickle.routes.globalRoute.portsCoordinates[1]

            let southWest = L.latLng(portStartCoordinates.lat, portStartCoordinates.lon),
                northEast = L.latLng(portFinishCoordinates.lat, portFinishCoordinates.lon),
                bounds = L.latLngBounds(southWest, northEast);

            this.map.fitBounds(bounds)

            post_text = {
                useAutoRoute: isAutoRoute,
                userecommendedTracks: recommended,
                restrictedZones: nogoZones,
                speedKnots: speed,
                points: [],
                from: portStart,
                to: portFinish,
                depth: nogo,
                height: height,
                width: width,
                length: length,
                defaultXteLeft: xte_l,
                defaultXteRight: xte_r,
                defaultRadius: radius,
                waitSeconds: 0,
                recommendedTrackCost: ratio,
                shallowWaterCoeff: shallowratio,
                shallowWaterCost: shallowstrength,
                avoidDistance: avoiddistance,
                generalRouteRefineCoeff: refinecoeff,
                allowSuezCanal: usesuez,
                allowPanamaCanal: usepanama,
                allowBeringStrait: usebering,
                allowRivers: userivers,
                generalRouteOnly: generalroutesonly,
                restrictedIds: [],
                restrictedCategories: [],
                restoreRoutePoints: gRoute,
                skipSafetyChecks: skipSafetyChecks,
                mssPreview: skipSafetyChecks
            }
        } else {
            post_text = {
                useAutoRoute: isAutoRoute,
                userecommendedTracks: recommended,
                restrictedZones: nogoZones,
                speedKnots: speed,
                points: pts,
                depth: method === 'graph' ? draught : nogo, // TODO: why nogo is used for depth?
                height: height,
                width: width,
                length: length,
                defaultXteLeft: xte_l,
                defaultXteRight: xte_r,
                defaultRadius: radius,
                waitSeconds: 0,
                recommendedTrackCost: ratio,
                shallowWaterCoeff: shallowratio,
                shallowWaterCost: shallowstrength,
                avoidDistance: avoiddistance,
                generalRouteRefineCoeff: refinecoeff,
                allowSuezCanal: usesuez,
                allowPanamaCanal: usepanama,
                allowBeringStrait: usebering,
                allowRivers: userivers,
                generalRouteOnly: generalroutesonly,
                restrictedIds: [],
                restrictedCategories: [],
                restoreRoutePoints: restoreRoutePoints,
                graphParams: graphParams,
                vesselParams: vesselParams,
                skipSafetyChecks: skipSafetyChecks,
                routeGraphPath: (method === 'graph' && generalRouteModified === false && generalRoute) ? generalRoute : null 
            }
        }
        
        return { URL, post_text };
    }

    startRouteCalculation(
        pts, 
        zones, 
        isAutoRoute = true, 
        isGlobalRoute = false, 
        isBypass = false, 
        type = 'none', 
        method = 'standard'
    ) {
        const data = this.routeCalculationURLAndPayload({
            pts,
            isAutoRoute,
            isGlobalRoute,
            type,
            method
        });

        if (!data) {
            this.clearRoute(false, true);
            return;
        }

        this.routesStore.resetShipDates();

        const { URL, post_text } = data;

        this.copyToClipboard(post_text);

        myAxios.get("api/v1/route/state").then(resp => {
            myAxios.post(URL, post_text)
                .then(resp => {
                    if (resp.data.errorCode === 0 && resp.data.id) {
                        store.commit("routes/setCalculatingStatus", true)
                        this.getRoute(resp.data.id, method)
                    } else {
                        alert('Error: (' + resp.data.errorCode + ') Can\'t compute routes at the moment');
                    }
                }).catch((error) => {
                if (isGlobalRoute)
                    alert('Error: (' + error + ') \nCan\'t compute routes by ports at the moment');
                else
                    alert('Error: (' + error.status + ') \nCan\'t compute routes at the moment');

                this.removeRouteWithDebug()
                this.restoreContextAfterAuto()
            })
        }).catch(error => {
            if (error.response.status === 503) {
                alert("No free slots for route calculating, try later")
                this.removeRouteWithDebug()
                this.restoreContextAfterAuto()
            } else {
                alert("ERROR CHECK: 1024")
            }

        })
    }

    startBypassCalculation(type) {
        let pts = []

        this.routesStore.backupShipResponse(this.bypassID1, this.bypassID2)


        pts.push({
            lat: this.waypoints[this.bypassID1].getLatLng().lat,
            lon: this.waypoints[this.bypassID1].getLatLng().lng,
            radius: this.routesStore.getShipRadius,
            xteLeft: this.routesStore.getShipXtel,
            xteRight: this.routesStore.getShipXter
        })

        pts.push({
            lat: this.waypoints[this.bypassID2].getLatLng().lat,
            lon: this.waypoints[this.bypassID2].getLatLng().lng,
            radius: this.routesStore.getShipRadius,
            xteLeft: this.routesStore.getShipXtel,
            xteRight: this.routesStore.getShipXter
        })

        this.startRouteCalculation(pts, [], true, false, true, type)
    }

    calculateByPass(wpID1, wpID2) {
        let bounds = L.latLngBounds(this.waypoints[wpID1].getLatLng(), this.waypoints[wpID2].getLatLng());
        this.map.fitBounds(bounds)

        if (wpID2 > wpID1) {
            this.bypassID1 = wpID1
            this.bypassID2 = wpID2
        } else {
            this.bypassID1 = wpID2
            this.bypassID2 = wpID1
        }

        eventBus.$emit('showCalculateByPassDialog')
    }

    /* Async API*/

    async syntheticProgressUpdate(progress) {
        while (store.state.routes.progress.isCalculating) {
            store.commit("routes/setProgress", {progress: progress.progress, text: progress.progressMessage})
            progress.progress++;
            await this.sleep(1000);
        }
    }

    async portsRouteAsync(config) {
        const { 
            startPort, 
            finishPort, 
            routePorts, 
            routePortsCoordinates,
            graphRoute = true,
            globalRoute = true,
            skipSafetyChecks = true
        } = config || {};

        if (!startPort || !finishPort || !routePorts || !routePortsCoordinates)  
            return false;

        let startTime = Date.now()
        console.log(store);

        store.commit("routes/setCalculatingStatus", { status: true, title: 'route.calculation' });

        const routeDepthChecksResult = await this.routeDepthChecks({
            minDepth: this.routesStore.getShipNoGo,
            startPoint: startPort,
            finishPoint: finishPort
        });

        if (routeDepthChecksResult) {
            this.clearRoute(true, true);  
            store.commit("routes/setRouteValidationMessage", routeDepthChecksResult);
            store.commit("routes/setCalculatingStatus", false)
            return false;
        }

        eventBus.$emit('switch-to-general-route'); 

        let progress = {
            progress: 0,
            progressMessage: ""
        }
        this.syntheticProgressUpdate(progress);

        try {
            const versionStore = useVersionStore();
            await versionStore.checkAppVersion();

            if (graphRoute) {
                progress.progressMessage = "Calculating general route, please wait ...";

                await this.calculateGraphRouteAsync({
                    type: 'preview',
                    startPoint: startPort,
                    endPoint: finishPort
                });

                progress.progress = 50;
            }

            if (globalRoute) {
                progress.progressMessage = "Calculating big data route, please wait ...";

                store.commit("fickle/setGlobalRouteParams", {
                    ports: routePorts, 
                    coordinates: routePortsCoordinates,
                })

                await this.startRouteCalculationAsync({
                    pts: [], 
                    zones: [], 
                    isAutoRoute: true, 
                    isGlobalRoute: true,
                    skipSafetyChecks: skipSafetyChecks,
                    updateProgress: false
                });
            }
        }
        catch (error) {
            this.showError(error);
        }
        store.commit("routes/setCalculatingStatus", false)
                        
        this.drawMapLayers();

        let time = Date.now() - startTime
        eventBus.$emit('show-calculate-time', time / 1000);
    
        return true;
    }

    async calculateGraphRouteAsync(config) {
        const routesStore = useRoutesStore();
        const { type, startPoint, endPoint } = config;
        const data = this.graphRouteURLAndPayload({ type, startPoint, endPoint });
        if (!data) {
            this.clearRoute(false, true);  
            alert('ERROR');
            return;
        }

        const { URL, post_text } = data;
        const graphRouteResponse = await myAxios.post(URL, post_text);

        routesStore.setGeneralRoute(graphRouteResponse.data);
        // copy general route route point to waypoints
        this.makeWaypointsFromGeneralRoute();
        this.drawMapLayers();
        this.fitBounds();
    }

    makeWaypointsFromGeneralRoute() {
        const routesStore = useRoutesStore();

        routesStore.resetShipDates();

        const generalRoute = routesStore.generalRoute.data;

        this.waypoints = [];
        
        if (!generalRoute) return;
        
        const wayPoints = [];

        const addWaypoint = (p) => {
            if (!p) return;

            if (!wayPoints.length || !GeoUtils.isSamePoint(p, wayPoints[wayPoints.length - 1])) {
                wayPoints.push(GeoUtils.normalizedPoint(p));
            }
        }

        const addOrthodromicPoints = (points) => {
            (points || []).filter(p => p.isOrthodromicPoint).forEach(p => {
                addWaypoint(p);
            });
        }

        const addPoints = (points) => {
            (points || []).forEach(p => {
                addWaypoint(p);
            });
        }

        const outgoingLink = (point) => {
            return generalRoute.links.find(l => GeoUtils.isSamePoint(point, l.points[0]));
        }

        const incomingLink = (point) => {
            return generalRoute.links.find(l => GeoUtils.isSamePoint(point, l.points[l.points.length - 1]));
        }

        addWaypoint(generalRoute.startPoint);

        if (generalRoute.links.some(l => l.points.some(p => p.isOrthodromicPoint))) {
            const startLink = outgoingLink(generalRoute.startPoint);
            addOrthodromicPoints(startLink?.points);

            // long distance route, add orthodromic points as well
            if (generalRoute.lines.length) {
                for (let lineIdx = 0; lineIdx < generalRoute.lines.length; lineIdx++) {
                    const line = generalRoute.lines[lineIdx]
                    addPoints(line.points);
                    const nextLink = outgoingLink(line.points[line.points.length - 1]);
                    addOrthodromicPoints(nextLink?.points);
                }
            }
        }
        else {
            // local area route
            generalRoute.lines.forEach(line => {
                addPoints(line.points);
            });
        }

        addWaypoint(generalRoute.finishPoint);

        // push all points into waypoints
        wayPoints.forEach(p => {
            waypoints.addWP.call(this, L.latLng(p.lat, p.lon), -2);
        })

        //this.makeRouteTable(true);
        legs.drawLegs.call(this);
    }

    async startRouteCalculationAsync(config) {
        const {
            pts, 
            restoreRoutePoints,
            zones, 
            isAutoRoute = true, 
            isGlobalRoute = false, 
            skipSafetyChecks = false,
            isBypass = false, 
            type = 'none', 
            method = 'standard',
            updateProgress = true,
            progressType,
            updateStore,
            progressOffset,
            progressFactor,
            waitForCompletion = true
        } = config;

        const data = this.routeCalculationURLAndPayload({
            pts,
            restoreRoutePoints,
            isAutoRoute,
            isGlobalRoute,
            skipSafetyChecks,
            type,
            method
        });

        if (!data) {
            this.clearRoute(false, true);
            return;
        }

        try {
            const { URL, post_text } = data;

            this.routesStore.resetShipDates();

            this.copyToClipboard(post_text);

            const resp = await myAxios.post(URL, post_text);
            
            if (!waitForCompletion) return resp.data;

            return await this.getRouteAsync({
                routeId: resp.data.id, 
                method,
                updateProgress,
                progressType,
                updateStore,
                progressOffset,
                progressFactor
            });
        }
        catch (error) {
            this.showError(error.data)
        }
    }

    async getRouteAsync(config) {
        const { 
            routeId, 
            method = 'standard', 
            updateProgress, 
            progressType, 
            updateStore, 
            progressFactor,
            progressOffset
        } = config;
        
        let data = await this.checkRouteAsync({ routeId, method, updateProgress, progressType, updateStore, progressFactor, progressOffset });
        while (data.status == "calculating") 
        {
            if (this.isAutoroutingCancel) {
                this.isAutoroutingCancel = false;
                // TODO: check endpoint
                await myAxios.delete("api/v1/graphRoute/" + routeId);
                break;
            }

            await this.sleep(1000);
            data = await this.checkRouteAsync({ routeId, method, updateProgress, progressType, updateStore, progressFactor, progressOffset });
        }
        return data;
    }

    async checkRouteAsync(config) {
        const { 
            routeId, 
            method = 'standard',
            updateProgress = true,
            progressType = "Calculating route",
            updateStore = true,
            progressOffset = 0,
            progressFactor = 1
        } = config;

        let URL = method === 'standard' ? '/api/v1/route/' : '/api/v1/graphRoute/';
        try {
            const resp = await myAxios.get(URL + routeId)

            let progressMessage = resp.data.progressMessage || '';
            const debugMarkers = ["Waiting for route", "Forward wave"];
            if (debugMarkers.some(m => progressMessage.startsWith(m))) {
                const routesStore = useRoutesStore();
                const graphDiag = routesStore?.getShipSettings?.graphDiag;

                if (!graphDiag) {
                    // this is diagnostic message, replace it with time counter
                    progressMessage = `${progressType}, please wait ... ${new Date().toLocaleTimeString()}`;
                }
            }
            
            if (updateProgress) {
                store.commit("routes/setProgress", {
                    progress: resp.data.progress >= 0 ? Math.floor(progressOffset + Math.max(0, resp.data.progress) * progressFactor) : null, 
                    text: progressFactor != 1 && resp.data.progress === 100 ? undefined : progressMessage // do not update progress with completed messaged when multi-stage operation is in progress
                })
            }

            if (resp.data.status === 'calculating' && this.isAutoroutingCancel) {
                this.restoreContextAfterAuto();
                return;
            }

            if (updateStore) {
                if ((resp.data.status === 'calculating' || resp.data.status === 'done') && resp.data.restoreRoutePoints.length > 0) {
                    this.routesStore.addBigDataRoute(resp.data.restoreRoutePoints)
                    this.drawMapLayers()
                }

                if (resp.data.status === 'calculating' && resp.data.routeLegs.length > 0) {
                    this.routesStore.addRouteLegs(resp.data.routeLegs)
                } 
            }

            return resp.data;
        }
        catch (error) {
            this.showError(error.data)
        }
    } 

    fitBounds() {
        const { isGeneralRoute, isNavRoute } = this.routesStore.activeRouteTabInfo ?? {};
        const generalRoute = this.routesStore.generalRoute?.data;
        const settings = this.routesStore?.getShipSettings ?? {};
        const graphDiag = settings?.graphDiag;

        if (isGeneralRoute && generalRoute) {
            const allPoints = [
                generalRoute.startPoint,
                ...generalRoute.lines.flatMap(line => line.points),
                ...generalRoute.links.flatMap(link => link.points),
                generalRoute.finishPoint
            ];

            if (graphDiag) {
                if (generalRoute.entryPoints && generalRoute.entryPoints.length > 0) {
                    allPoints.push(...generalRoute.entryPoints);
                }
                if (generalRoute.exitPoints && generalRoute.exitPoints.length > 0) {
                    allPoints.push(...generalRoute.exitPoints);
                }
            }

            const sameHemiPoints = allPoints.map(p => GeoUtils.toPointHemisphere(allPoints, p));

            const bounds = L.latLngBounds(
                sameHemiPoints.map(point => [point.lat, point.lon])
            );
            this.map.fitBounds(bounds);

            return;
        }

        if (isNavRoute && this.waypoints.length) {
            const allPoints = this.waypoints.map(point => point.getLatLng());
            const sameHemiPoints = allPoints.map(p => GeoUtils.toPointHemisphere(allPoints, p));
            const bounds = L.latLngBounds(
                sameHemiPoints.map(point => [point.lat, point.lon])
            );
            this.map.fitBounds(bounds);
            return;
        }
    }
}
